import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { Region } from "../../../models/region-model";
import { SelectorCountry } from '../../../models/selector-country.model';

@Component({
  selector: 'mpro-custom-region',
  templateUrl: './custom-region.component.html',
  styleUrls: ['./custom-region.component.scss']
})
export class CustomRegionComponent implements OnInit, OnDestroy {

  public availableCountries: SelectorCountry[] = [];
  public selectedCountries: Map<string, SelectorCountry> = new Map();
  private searchSubject: Subject<void> = new Subject();

  inputState: "default" | "error" = "default"

  searchTerm: string = ""

  @Input() region: Region;
  @Input() public countries: Map<string, SelectorCountry> = new Map();
  @Input() public preSelectedCountries: string[] = [];

  @Output() save = new EventEmitter()
  @Output() cancel = new EventEmitter()

  noName: boolean = false

  private ngUnsubscribe: Subject<void> = new Subject();

  ngOnInit(): void {
    this.availableCountries = [...this.countries.values()];

    if (!this.region.id) {
      this.region = new Region('');
      this.selectCountries(this.preSelectedCountries.slice());
    } else {
      this.selectCountries(this.region.countries);
    }

    this.searchSubject
      .pipe(
        takeUntil(this.ngUnsubscribe),
        debounceTime(300)
      )
      .subscribe(() => this.search())
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public onCountrySelected(checked: boolean, country: SelectorCountry) {
    if (checked) {
      this.selectedCountries.set(country.code, country);
    } else {
      this.selectedCountries.delete(country.code);
    }
  }

  regionNameKeyup() {
    this.inputState = "default"
  }

  searchTermKeyup() {
    if (this.searchTerm === "") {
      this.availableCountries = [...this.countries.values()];
    }

    this.searchSubject.next();
  }

  clearSearch() {
    this.availableCountries = [...this.countries.values()];
  }

  search() {
    this.availableCountries = [...this.countries.values()].filter(country => {
      const indexOfSubstr = country.name.toLocaleLowerCase().indexOf(this.searchTerm.toLocaleLowerCase());
      return indexOfSubstr !== -1 ? true : false;
    });
  }

  saveClick() {
    if (this.region.name.length === 0) {
      this.noName = true
      this.inputState = "error"
    } else if (this.region.name.length > 16) {
      this.noName = false
      this.inputState = "error"
    } else {
      // Save
      this.region.countries = Array.from(this.selectedCountries.keys());
      this.save.emit(this.region);
    }
  }

  cancelClick() {
    this.cancel.emit()
  }

  public getSortedCountries(): SelectorCountry[] {
    return [...this.availableCountries].sort((c1, c2) => {
      if (this.selectedCountries?.has(c1.code)
        && this.selectedCountries?.has(c2.code)) {
        return c1.name.localeCompare(c2.name);
      }

      if (this.selectedCountries?.has(c1.code)) {
        return -1;
      }

      if (this.selectedCountries?.has(c2.code)) {
        return 1;
      }

      return c1.name.localeCompare(c2.name);
    });
  }

  private selectCountries(codes: string[]) {
    codes.forEach(country => {
      let selectorCountry = this.countries.get(country);
      if (selectorCountry) {
        this.selectedCountries.set(country, selectorCountry);
      }
    });
  }
}
